import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, Heart, Moon, Zap, TrendingUp, RefreshCw, 
  CheckCircle, XCircle, ArrowLeft, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function OuraPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ connected: false, configured: false, last_sync: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [healthData, setHealthData] = useState(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, healthRes] = await Promise.all([
        api.get("/oura/status"),
        api.get("/oura/today").catch(() => ({ data: {} })),
      ]);
      setStatus(statusRes.data);
      setHealthData(healthRes.data);
    } catch (err) {
      console.error("Oura status error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await api.get("/oura/auth-url");
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      console.error("Auth URL error:", err);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/oura/disconnect");
      setStatus({ ...status, connected: false });
      setHealthData(null);
    } catch (err) {
      console.error("Disconnect error:", err);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/oura/sync");
      const res = await api.get("/oura/today");
      setHealthData(res.data);
      const statusRes = await api.get("/oura/status");
      setStatus(statusRes.data);
    } catch (err) {
      console.error("Sync error:", err);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12 max-w-3xl mx-auto" data-testid="oura-page">
      <button
        onClick={() => navigate("/connections")}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Connections
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500/20 to-rose-600/20 flex items-center justify-center">
          <Activity className="w-8 h-8 text-rose-400" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
            Oura Ring
          </h1>
          <p className="text-slate-500">Sync your health and wellness data</p>
        </div>
      </div>

      {!status.configured ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6"
        >
          <h3 className="text-amber-400 font-semibold mb-2">Oura App Not Configured</h3>
          <p className="text-sm text-slate-400">
            To enable Oura Ring integration, you need to add your Oura API credentials.
            Visit the{" "}
            <a
              href="https://cloud.ouraring.com/oauth/applications"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00FF88] underline"
            >
              Oura Developer Console
            </a>{" "}
            to create an application.
          </p>
        </motion.div>
      ) : !status.connected ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.1] rounded-2xl p-8 text-center"
        >
          <Activity className="w-16 h-16 text-rose-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
            Connect Your Oura Ring
          </h2>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
            Sync your sleep, activity, heart rate, and readiness scores to track your health journey.
          </p>
          <Button
            onClick={handleConnect}
            className="bg-rose-500 hover:bg-rose-600 text-white rounded-full px-8 h-12"
            data-testid="connect-oura-btn"
          >
            <Activity className="w-4 h-4 mr-2" />
            Connect with Oura
          </Button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Connected Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-sm font-semibold text-emerald-400">Connected</p>
                  <p className="text-xs text-slate-500">
                    Last synced: {status.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSync}
                  disabled={syncing}
                  variant="outline"
                  className="rounded-full border-white/10 text-white hover:bg-white/10"
                  size="sm"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Now
                </Button>
                <Button
                  onClick={handleDisconnect}
                  variant="outline"
                  className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                  size="sm"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Health Stats */}
          {healthData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.1] rounded-2xl p-6"
            >
              <h3 className="text-sm font-semibold text-white mb-4">Today's Health Data</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <Heart className="w-5 h-5 text-rose-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{healthData.heart_rate || '--'}</p>
                  <p className="text-xs text-slate-500">Avg Heart Rate</p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{healthData.steps?.toLocaleString() || '--'}</p>
                  <p className="text-xs text-slate-500">Steps</p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <Moon className="w-5 h-5 text-indigo-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{healthData.sleep_score || '--'}</p>
                  <p className="text-xs text-slate-500">Sleep Score</p>
                </div>
                
                <div className="p-4 rounded-xl bg-white/5 text-center">
                  <Zap className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{healthData.readiness_score || '--'}</p>
                  <p className="text-xs text-slate-500">Readiness</p>
                </div>
              </div>

              {healthData.sleep_duration_hours && (
                <div className="mt-4 p-4 rounded-xl bg-white/5">
                  <p className="text-sm text-slate-400">
                    You slept <span className="text-white font-semibold">{healthData.sleep_duration_hours} hours</span> last night
                    {healthData.resting_heart_rate && (
                      <>, with a resting heart rate of <span className="text-white font-semibold">{healthData.resting_heart_rate} BPM</span></>
                    )}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.1] rounded-2xl p-6"
          >
            <h3 className="text-sm font-semibold text-white mb-4">What We Sync</h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-3">
                <Moon className="w-4 h-4 text-indigo-400" />
                Sleep duration, quality, and sleep stages
              </li>
              <li className="flex items-center gap-3">
                <Activity className="w-4 h-4 text-emerald-400" />
                Daily activity, steps, and calories burned
              </li>
              <li className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-rose-400" />
                Heart rate throughout the day
              </li>
              <li className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-amber-400" />
                Readiness scores and recovery metrics
              </li>
            </ul>
          </motion.div>
        </div>
      )}
    </div>
  );
}
